import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const AnalyticsPageSkeleton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <SkeletonTheme baseColor="#f0f0f0" highlightColor="#e0e0e0">
      <Box
        sx={{
          minHeight: '100vh',
          background: '#f8fafc',
        }}
      >
        <Box
          sx={{
            background: '#ffffff',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            borderRadius: '0 0 32px 32px',
            p: { xs: 2, sm: 3, md: 5 },
            mb: { xs: 2, md: 4 },
          }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2
          }}>
            <Skeleton circle width={isMobile ? 48 : 60} height={isMobile ? 48 : 60} />
            <Box sx={{ flex: 1 }}>
              <Skeleton height={isMobile ? 28 : 40} width={isMobile ? 180 : 250} style={{ marginBottom: 8 }} />
              <Skeleton height={isMobile ? 16 : 20} width={isMobile ? '100%' : 350} />
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 4 } }}>
            {[...Array(4)].map((_, index) => (
              <Grid item xs={6} sm={6} lg={3} key={index}>
                <Card sx={{
                  borderRadius: '24px',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                }}>
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between',
                      mb: 2,
                      gap: 1
                    }}>
                      <Skeleton circle width={isMobile ? 48 : 60} height={isMobile ? 48 : 60} />
                      <Skeleton height={28} width={70} style={{ borderRadius: '12px' }} />
                    </Box>
                    <Skeleton height={isMobile ? 28 : 36} width={isMobile ? 80 : 120} style={{ marginBottom: 8 }} />
                    <Skeleton height={isMobile ? 14 : 16} width={isMobile ? '100%' : 150} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} lg={6}>
              <Card
                sx={{
                  borderRadius: '24px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 2,
                    mb: { xs: 2, md: 4 }
                  }}>
                    <Skeleton circle width={isMobile ? 40 : 50} height={isMobile ? 40 : 50} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton height={isMobile ? 22 : 28} width={isMobile ? 180 : 250} style={{ marginBottom: 4 }} />
                      <Skeleton height={isMobile ? 14 : 16} width={isMobile ? 150 : 200} />
                    </Box>
                  </Box>

                  {[...Array(3)].map((_, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        mb: 1,
                        gap: 1
                      }}>
                        <Skeleton height={20} width={100} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Skeleton height={16} width={80} />
                          <Skeleton height={24} width={50} style={{ borderRadius: '12px' }} />
                        </Box>
                      </Box>
                      <Skeleton height={10} style={{ borderRadius: '5px' }} />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Card
                sx={{
                  borderRadius: '24px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 2,
                    mb: { xs: 2, md: 4 }
                  }}>
                    <Skeleton circle width={isMobile ? 40 : 50} height={isMobile ? 40 : 50} />
                    <Box>
                      <Skeleton height={isMobile ? 22 : 28} width={isMobile ? 140 : 180} style={{ marginBottom: 4 }} />
                      <Skeleton height={isMobile ? 14 : 16} width={isMobile ? 180 : 220} />
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 } }}>
                    {[...Array(2)].map((_, index) => (
                      <Paper
                        key={index}
                        sx={{
                          p: { xs: 2, md: 3 },
                          borderRadius: '16px',
                          background: '#f8fafc',
                          border: '1px solid rgba(0, 0, 0, 0.06)',
                          boxShadow: 'none',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                          <Skeleton circle width={24} height={24} />
                          <Skeleton height={isMobile ? 18 : 24} width={isMobile ? 140 : 180} />
                        </Box>
                        <Skeleton height={isMobile ? 28 : 36} width={100} style={{ marginBottom: 8 }} />
                        <Skeleton height={isMobile ? 14 : 16} width={isMobile ? '100%' : 220} />
                      </Paper>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card
            sx={{
              borderRadius: '24px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              mt: 3,
              textAlign: 'center',
              p: { xs: 2, md: 4 },
            }}
          >
            <Skeleton circle width={isMobile ? 60 : 80} height={isMobile ? 60 : 80} style={{ margin: '0 auto 24px' }} />
            <Skeleton height={isMobile ? 24 : 32} width={isMobile ? '80%' : 300} style={{ margin: '0 auto 16px' }} />
            <Skeleton height={isMobile ? 16 : 20} width={isMobile ? '100%' : 500} style={{ margin: '0 auto 24px' }} />
            <Skeleton height={36} width={isMobile ? 150 : 180} style={{ margin: '0 auto', borderRadius: '18px' }} />
          </Card>
        </Box>
      </Box>
    </SkeletonTheme>
  );
};

export default AnalyticsPageSkeleton;
